import {globalAudioManager} from '@wonderlandengine/spatial-audio';
import { gameState } from '../state/GameState.js';
import { Player, PlayerStates } from '../state/Player.js';
import { Enemy, EnemyStates } from '../state/Enemy.js';

const sfx = 'audio/sfx/';

export enum Sounds {
    ButtonPress,
    Portal,
    ElectricityLoop,
    Footsteps,
    Notification,
    PowerUp,
    PunchSwoosh,
    SkeletonCrack,
    SkeletonGrowl,
    SkeletonMoan,
    SkeletonHit,
    FleshHit,
    UiPopup,
    Dash,
    BlingGood,
    BlingBad,
    GameOver,
    Achievement,
    Punch,
    SkeletonGolemGrunt,
    WerewolfAggro,
    WerewolfDead,
    OrcDead,
    OrcAggro,
    MageAggro,
    GolemWalk,
    GolemDead,
    Revive,
}

export type PlayConfig = {
    /** Sets the volume of the player (0-1) */
    volume?: number;
    /** Whether to loop the audio */
    loop?: boolean;
    /**
     * Sets the position of the audio source and makes it spatial.
     *
     * @note Panned audio will always use HRTF for spatialization.
     * @warning For this to work correctly, the audio-listener needs to be set up!
     */
    position?: Float32Array;
    /** Sets the channel on which the audio will be played. */
    channel?: number;
    /**
     * Whether the audio has priority or not. If not, playback can be stopped to free up a player when no others are
     * available.
     */
    priority?: boolean;
};

export function playSafe(id: Sounds, config: PlayConfig): number {
    try{
        return globalAudioManager.play(id, config); 
    } catch {
        return -1;
    }
}

export function playOneShotSafe(id: Sounds, config: any) {
    try{
        globalAudioManager.playOneShot(id, config); 
    } catch {
    }
}

export function loadAudio() {
    globalAudioManager.loadBatch(
        [sfx + 'button_press.mp3', Sounds.ButtonPress],
        [sfx + 'footsteps.mp3', Sounds.Footsteps],
        [sfx + 'gameover.mp3', Sounds.GameOver],
        [sfx + 'revive.mp3', Sounds.Revive],
        [sfx + 'achievement.mp3', Sounds.Achievement],
        [sfx + 'golem_dead.mp3', Sounds.GolemDead],
        [sfx + 'mage_aggro.mp3', Sounds.MageAggro],
        [sfx + 'portal.mp3', Sounds.Portal],
        [sfx + 'punch.mp3', Sounds.Punch],
        [
            [
                sfx + 'punch_swoosh0.mp3',
                sfx + 'punch_swoosh1.mp3',
                sfx + 'punch_swoosh2.mp3',
            ],
            Sounds.PunchSwoosh,
        ],
        [
            [
                sfx + 'skeleton_moan0.mp3',
                sfx + 'skeleton_moan1.mp3',
                sfx + 'skeleton_moan2.mp3',
            ],
            Sounds.SkeletonMoan,
        ],
        [
            [
                sfx + 'skeleton_crack0.mp3',
                sfx + 'skeleton_crack1.mp3',
                sfx + 'skeleton_crack2.mp3',
                sfx + 'skeleton_crack3.mp3',
                sfx + 'skeleton_crack4.mp3',
                sfx + 'skeleton_crack5.mp3',
                sfx + 'skeleton_crack6.mp3',
            ],
            Sounds.SkeletonCrack,
        ],
        [
            [
                sfx + 'skeleton_hit0.mp3',
                sfx + 'skeleton_hit1.mp3',
                sfx + 'skeleton_hit2.mp3',
                sfx + 'skeleton_hit3.mp3',
                sfx + 'skeleton_hit4.mp3',
                sfx + 'skeleton_hit5.mp3',
            ],
            Sounds.SkeletonHit,
        ],
        [
            [
                sfx + 'flesh_hit0.mp3',
                sfx + 'flesh_hit1.mp3',
                sfx + 'flesh_hit2.mp3',
                sfx + 'flesh_hit3.mp3',
            ],
            Sounds.FleshHit,
        ],
        [sfx + 'skeleton_growl.mp3', Sounds.SkeletonGrowl],
        [sfx + 'werewolf_aggro.mp3', Sounds.WerewolfAggro],
        [sfx + 'werewolf_dead.mp3', Sounds.WerewolfDead],
        [sfx + 'orc_aggro.mp3', Sounds.OrcAggro],
        [sfx + 'skeleton_growl.mp3', Sounds.SkeletonGrowl],
        [sfx + 'skeleton_golem_grunt.mp3', Sounds.SkeletonGolemGrunt],
        [sfx + 'button_press.mp3', Sounds.ButtonPress],
        [sfx + 'ui_popup.mp3', Sounds.UiPopup],
        [sfx + 'powerup.mp3', Sounds.PowerUp],
        [sfx + 'notification.mp3', Sounds.Notification],
        [[sfx + 'dash0.mp3', sfx + 'dash1.mp3'], Sounds.Dash],
        [
            [sfx + 'bling_bad0.mp3', sfx + 'bling_bad1.mp3', sfx + 'bling_bad2.mp3'],
            Sounds.BlingBad,
        ]
    );
}

let footsteps = -1;

export function addPlayerAudio(player: Player) {
        player.onRevive.add(() => {
            playOneShotSafe(Sounds.Revive, {volume: 0.3});
        })
        player.propertyChanged.add(() => {
            switch (gameState.currentPlayer.state) {
                case PlayerStates.Move:
                    if (footsteps == -1) {
                        footsteps = playSafe(Sounds.Footsteps, {
                            loop: true,
                            volume: 0.2
                        });
                    }
                    break;
                case PlayerStates.Dashing:
                    playOneShotSafe(Sounds.Dash, {
                        volume: 0.4
                    })
                    break;
                case PlayerStates.Idle:
                    globalAudioManager.stop(Sounds.Footsteps, footsteps);
                    footsteps = -1;
                    break;
                case PlayerStates.Hit:
                    playOneShotSafe(Sounds.BlingBad, {volume: 0.2});
                    break;
                case PlayerStates.Attack:
                    globalAudioManager.stop(Sounds.Footsteps, footsteps);
                    footsteps = -1;
                    playOneShotSafe(Sounds.PunchSwoosh, {volume: 0.4});
                    break;
                case PlayerStates.Dead:
                    globalAudioManager.stop(Sounds.Footsteps, footsteps);
                    footsteps = -1;
                    break;
                default:
            }

        });
}

let idCount = 0;

export function addEnemyAudio(enemy: Enemy) {
    const myID = idCount;
    const type = enemy.name;
    enemy.propertyChanged.add(() => {
        switch (enemy.state) {
            case EnemyStates.Attack:
                break;
            case EnemyStates.Hit:
                playOneShotSafe(Sounds.Punch, {volume: 0.5});
                switch(type){
                    case 'Skeleton Minion':
                    case 'Skeleton Mage':
                    case 'Skeleton Warrior':
                    case 'Ice-Skeleton Minion':
                    case 'Skeleton Golem':
                        playOneShotSafe(Sounds.SkeletonHit, {volume: 0.5});
                        break;
                    default:
                        playOneShotSafe(Sounds.FleshHit, {volume: 0.3});
                }
                break;
            case EnemyStates.Idle:
                break;
            case EnemyStates.Dead:
                switch(type){
                    case 'Skeleton Minion':
                    case 'Skeleton Mage':
                    case 'Skeleton Warrior':
                    case 'Ice-Skeleton Minion':
                        playOneShotSafe(Sounds.Punch, {volume: 0.5});
                        playOneShotSafe(Sounds.SkeletonCrack, {volume: 0.4});
                        break;
                    case 'Skeleton Golem':
                        playOneShotSafe(Sounds.Punch, {volume: 0.5});
                        playOneShotSafe(Sounds.GolemDead, {volume: 0.4});
                        break;
                    case 'Werewolf':
                        playOneShotSafe(Sounds.WerewolfDead, {volume: 0.3});
                        break;
                }
                // Dead state is called twice when a enemy dies
                enemy.propertyChanged.remove('audio'+myID)
                break;
            case EnemyStates.Aggro:
                switch(type){
                    case 'Skeleton Warrior':
                    case 'Skeleton Minion':
                        playOneShotSafe(Sounds.SkeletonMoan, {volume: 0.1});
                        break;
                    case 'Skeleton Mage':
                        playOneShotSafe(Sounds.MageAggro, {volume: 0.3});
                        break;
                    case 'Skeleton Golem':
                        playOneShotSafe(Sounds.SkeletonGolemGrunt, {volume: 0.3});
                        break;
                    case 'Orc':
                        playOneShotSafe(Sounds.OrcAggro, {volume: 0.2});
                        break;
                    case 'Werewolf':
                        playOneShotSafe(Sounds.WerewolfAggro, {volume: 0.2});
                        break;
                }
                break;
            default:
        }
    }, {id: 'audio'+myID});
    idCount++;
}

